import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Lock, Unlock, Ban, Zap } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { formatSlot, formatSlotShort } from "@/lib/formatSlot";

import { API } from "@/lib/apiBase";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Expand a base date into N consecutive same-weekday occurrences.
 *  e.g. base = "2026-05-22" (Fri), weeks = 4
 *       → ["2026-05-22","2026-05-29","2026-06-05","2026-06-12"] */
const expandWeeklyDates = (baseDateStr, weeks) => {
  const n = Math.max(1, Math.min(52, Number(weeks) || 1));
  const out = [];
  // Parse as local-noon to avoid TZ drift across month boundaries.
  const [y, m, d] = baseDateStr.split("-").map(Number);
  const base = new Date(y, m - 1, d, 12, 0, 0);
  for (let i = 0; i < n; i += 1) {
    const dt = new Date(base);
    dt.setDate(base.getDate() + i * 7);
    out.push(toDateStr(dt));
  }
  return out;
};

const AdminCalendar = ({ refreshTick }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [courtsData, setCourtsData] = useState([]);
  const [loading, setLoading] = useState(false);
  // selections keyed by court_id -> Set of time_slot strings
  const [selections, setSelections] = useState({});
  const [publicNote, setPublicNote] = useState("");
  const [privateNote, setPrivateNote] = useState("");
  // Recurring weekly blocks. 1 = no repeat, N = block this date + next N-1 weeks.
  const [repeatWeeks, setRepeatWeeks] = useState(1);
  // Mobile UX state — sidebar collapse + which court is visible on small screens.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileCourtIdx, setMobileCourtIdx] = useState(0);

  // Track the last date we successfully fetched for so we can clear the
  // user's slot selections ONLY when the date actually changes. Without
  // this guard, background refreshes (StrictMode double-effect + a
  // `refreshTick` prop toggle from the parent tab activation) wipe the
  // in-flight selection and the "Unblock selected" button never leaves
  // its disabled state — exactly matching the user-reported bug.
  const lastFetchedDateRef = useRef(null);

  const fetchDay = useCallback(async () => {
    if (!selectedDate) return;
    const nextDateStr = toDateStr(selectedDate);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/calendar/day?date=${nextDateStr}`
      );
      setCourtsData(res.data.courts || []);
      // Only reset selections when the admin actually moved to a new date.
      // Background refreshes for the same date must preserve whatever the
      // user has already clicked.
      if (lastFetchedDateRef.current !== nextDateStr) {
        setSelections({});
        lastFetchedDateRef.current = nextDateStr;
      }
    } catch (err) {
      // Surface the underlying error message when present so 5xx / network
      // issues are diagnosable, otherwise show the generic toast.
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `Failed to load calendar — ${detail}` : "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchDay();
  }, [fetchDay, refreshTick]);

  const toggleSelect = (courtId, slotStr, status) => {
    if (status !== "available" && status !== "manually_blocked") return;
    setSelections((prev) => {
      const next = { ...prev };
      const set = new Set(next[courtId] || []);
      if (set.has(slotStr)) set.delete(slotStr);
      else set.add(slotStr);
      next[courtId] = set;
      return next;
    });
  };

  const clearCourtSelection = (courtId) =>
    setSelections((prev) => ({ ...prev, [courtId]: new Set() }));

  // "OP PM" quick action — block 5 PM through 1 AM on the selected date.
  // 5 PM to 1 AM = 8 hourly slots: 17:00-18:00 through 00:00-01:00.
  // (The system stores the 0:00-1:00 slot on the same date as the evening
  // block, so this is a single-date operation.)
  const OP_SLOTS = [
    "17:00 - 18:00",
    "18:00 - 19:00",
    "19:00 - 20:00",
    "20:00 - 21:00",
    "21:00 - 22:00",
    "22:00 - 23:00",
    "23:00 - 00:00",
    "00:00 - 01:00",
  ];

  // "OP AM" quick action — block 8 AM through 12 PM (the morning Open Plays
  // window) on Courts 1, 2, 3 simultaneously. Court 4 is intentionally
  // excluded by venue policy. Slots = 08:00 through 11:00 starts (4 hourly
  // slots covering 8 AM - 12 PM).
  const OP_AM_SLOTS = [
    "08:00 - 09:00",
    "09:00 - 10:00",
    "10:00 - 11:00",
    "11:00 - 12:00",
  ];
  // Courts that participate in the multi-court OP AM/PM mass blocks (Court 4
  // is reserved for private rentals and excluded from Open Plays).
  const OP_AM_COURT_NAMES = ["Court 1", "Court 2", "Court 3"];
  const OP_PM_COURT_NAMES = ["Court 1", "Court 2", "Court 3"];

  const handleBlockOP = async (court) => {
    const dateStr = toDateStr(selectedDate);
    // Only block slots that are currently `available` — confirmed bookings
    // or GCal events should not be overwritten.
    const available = new Set(
      court.all_slots
        .filter((s) => s.status === "available")
        .map((s) => s.time_slot)
    );
    const toBlock = OP_SLOTS.filter((s) => available.has(s));
    if (!toBlock.length) {
      toast.error("No available 5 PM–1 AM slots on this day");
      return;
    }
    const skipped = OP_SLOTS.length - toBlock.length;
    const dates = expandWeeklyDates(dateStr, repeatWeeks);
    const repeatNote = dates.length > 1 ? ` × ${dates.length} weeks` : "";
    const msg =
      `Block ${toBlock.length} slot${toBlock.length > 1 ? "s" : ""} (Open Plays, 5 PM–1 AM)${repeatNote} ` +
      `on ${court.court_name} starting ${dateStr}?` +
      (skipped
        ? `\n\n(${skipped} slot${skipped > 1 ? "s" : ""} already booked/blocked on the base date — will skip.)`
        : "");
    if (!window.confirm(msg)) return;
    try {
      await Promise.all(
        dates.map((d) =>
          axios.post(`${API}/admin/blocks`, {
            court_id: court.court_id,
            booking_date: d,
            time_slots: toBlock,
            public_note: "OP",
            private_note: "Open Plays",
          })
        )
      );
      toast.success(`Open Plays blocked on ${court.court_name}${repeatNote} (${toBlock.length} slot${toBlock.length > 1 ? "s" : ""})`);
      fetchDay();
    } catch {
      toast.error("Failed to block Open Plays");
    }
  };

  // "OP AM" — multi-court quick action. Blocks 8 AM - 12 PM (4 slots) on
  // Courts 1, 2, 3 in a single click. Public + private notes are both
  // hard-coded to "OP" so customers see the same "OP" badge for AM and PM
  // open plays, and admins can sort the schedule by note in the Blocks tab.
  const handleBlockOPAM = async () => {
    const dateStr = toDateStr(selectedDate);
    const courtsToBlock = courtsData
      .filter((c) => OP_AM_COURT_NAMES.includes(c.court_name))
      .map((c) => {
        const available = new Set(
          c.all_slots.filter((s) => s.status === "available").map((s) => s.time_slot)
        );
        return { court: c, slots: OP_AM_SLOTS.filter((s) => available.has(s)) };
      })
      .filter(({ slots }) => slots.length > 0);

    if (!courtsToBlock.length) {
      toast.error("No available 8 AM–12 PM slots on Courts 1–3");
      return;
    }
    const total = courtsToBlock.reduce((n, { slots }) => n + slots.length, 0);
    const dates = expandWeeklyDates(dateStr, repeatWeeks);
    const repeatNote = dates.length > 1 ? ` × ${dates.length} weeks` : "";
    const ok = window.confirm(
      `Block Open Plays AM (8 AM–12 PM) — ${total} slot(s) across ` +
        `${courtsToBlock.length} court(s)${repeatNote} starting ${dateStr}?`
    );
    if (!ok) return;
    try {
      await Promise.all(
        dates.flatMap((d) =>
          courtsToBlock.map(({ court, slots }) =>
            axios.post(`${API}/admin/blocks`, {
              court_id: court.court_id,
              booking_date: d,
              time_slots: slots,
              public_note: "OP",
              private_note: "OP",
            })
          )
        )
      );
      toast.success(`OP AM blocked on ${courtsToBlock.length} court(s)${repeatNote}`);
      fetchDay();
    } catch {
      toast.error("Failed to block OP AM");
    }
  };

  // "OP PM (all courts)" — multi-court quick action. Blocks 5 PM - 1 AM (8
  // slots: 17:00 through 00:00 starts) on Courts 1, 2, 3 in a single click.
  // Mirrors handleBlockOPAM. Public + private notes hard-coded to "OP".
  const handleBlockOPPMMulti = async () => {
    const dateStr = toDateStr(selectedDate);
    const courtsToBlock = courtsData
      .filter((c) => OP_PM_COURT_NAMES.includes(c.court_name))
      .map((c) => {
        const available = new Set(
          c.all_slots.filter((s) => s.status === "available").map((s) => s.time_slot)
        );
        return { court: c, slots: OP_SLOTS.filter((s) => available.has(s)) };
      })
      .filter(({ slots }) => slots.length > 0);

    if (!courtsToBlock.length) {
      toast.error("No available 5 PM–1 AM slots on Courts 1–3");
      return;
    }
    const total = courtsToBlock.reduce((n, { slots }) => n + slots.length, 0);
    const dates = expandWeeklyDates(dateStr, repeatWeeks);
    const repeatNote = dates.length > 1 ? ` × ${dates.length} weeks` : "";
    const ok = window.confirm(
      `Block Open Plays PM (5 PM–1 AM) — ${total} slot(s) across ` +
        `${courtsToBlock.length} court(s)${repeatNote} starting ${dateStr}?`
    );
    if (!ok) return;
    try {
      await Promise.all(
        dates.flatMap((d) =>
          courtsToBlock.map(({ court, slots }) =>
            axios.post(`${API}/admin/blocks`, {
              court_id: court.court_id,
              booking_date: d,
              time_slots: slots,
              public_note: "OP",
              private_note: "OP",
            })
          )
        )
      );
      toast.success(`OP PM blocked on ${courtsToBlock.length} court(s)${repeatNote}`);
      fetchDay();
    } catch {
      toast.error("Failed to block OP PM");
    }
  };

  const handleBlockDay = async (court) => {
    const available = court.all_slots
      .filter((s) => s.status === "available")
      .map((s) => s.time_slot);
    if (!available.length) {
      toast.error("No available slots to block on this day");
      return;
    }
    if (!privateNote.trim()) {
      toast.error("Private note is required (e.g. client name)");
      return;
    }
    const dates = expandWeeklyDates(toDateStr(selectedDate), repeatWeeks);
    const repeatNote = dates.length > 1 ? ` for ${dates.length} weeks` : "";
    const ok = window.confirm(
      `Block ALL ${available.length} available slot(s) on ${court.court_name}${repeatNote} starting ${toDateStr(selectedDate)}?`
    );
    if (!ok) return;
    try {
      await Promise.all(
        dates.map((d) =>
          axios.post(`${API}/admin/blocks`, {
            court_id: court.court_id,
            booking_date: d,
            time_slots: available,
            public_note: publicNote || "Full day",
            private_note: privateNote.trim(),
          })
        )
      );
      toast.success(`Blocked full day on ${court.court_name}${repeatNote}`);
      setPublicNote("");
      setPrivateNote("");
      fetchDay();
    } catch {
      toast.error("Failed to block the day");
    }
  };

  const handleUnblockDay = async (court) => {
    const blocked = court.all_slots
      .filter((s) => s.status === "manually_blocked")
      .map((s) => s.time_slot);
    if (!blocked.length) {
      toast.error("No manual blocks to remove on this day");
      return;
    }
    // Same-day, whole-court clear is destructive — confirm first so the
    // admin doesn't wipe an entire day of manual blocks with one stray tap.
    const ok = window.confirm(
      `Clear all ${blocked.length} manual block(s) on ${court.court_name} for ${toDateStr(selectedDate)}?`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/blocks/unblock`, {
        court_id: court.court_id,
        booking_date: toDateStr(selectedDate),
        time_slots: blocked,
      });
      toast.success(`Cleared manual blocks on ${court.court_name}`);
      fetchDay();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `Failed to unblock — ${detail}` : "Failed to unblock the day");
    }
  };

  // Block every currently-selected slot across ALL courts in a single click.
  // Builds a per-court payload from the `selections` state (only `available`
  // slots — already-blocked/booked slots in the selection set are ignored).
  // Mandatory private note enforced.
  const handleBlockSelectedAcrossCourts = async () => {
    const dateStr = toDateStr(selectedDate);
    const payloads = courtsData
      .map((c) => {
        const sel = selections[c.court_id] || new Set();
        const availableRows = c.all_slots.filter(
          (s) => sel.has(s.time_slot) && s.status === "available"
        );
        const availableSlots = availableRows.map((s) => s.time_slot);
        const amount = availableRows.reduce(
          (n, s) => n + (Number(s.rate) || 0),
          0
        );
        return { court: c, slots: availableSlots, amount };
      })
      .filter(({ slots }) => slots.length > 0);

    if (!payloads.length) {
      toast.error("Select at least one available slot first");
      return;
    }
    if (!privateNote.trim()) {
      toast.error("Private note is mandatory (e.g. client name)");
      return;
    }
    const total = payloads.reduce((n, { slots }) => n + slots.length, 0);
    const totalAmount = payloads.reduce((n, { amount }) => n + amount, 0);
    // If either note is just "OP" (Open Play), don't stamp the amount —
    // OP blocks aren't paid rentals so the amount is meaningless.
    const isOP =
      publicNote.trim().toUpperCase() === "OP" ||
      privateNote.trim().toUpperCase() === "OP";
    const amountSuffix = totalAmount > 0 && !isOP
      ? ` · ₱${totalAmount.toLocaleString("en-PH")}`
      : "";
    // Append the collected total to the private note only — the public
    // note is what customers see, so it must never expose the internal
    // amount to be collected.
    const privateNoteWithAmount = `${privateNote.trim()}${amountSuffix}`;
    const publicNoteToSend = publicNote || null;
    const dates = expandWeeklyDates(dateStr, repeatWeeks);
    const repeatNote = dates.length > 1 ? ` × ${dates.length} weeks` : "";
    const ok = window.confirm(
      `Block ${total} slot(s) across ${payloads.length} court(s)${repeatNote} starting ${dateStr}${amountSuffix ? ` (${amountSuffix.trim().slice(2)})` : ""}?`
    );
    if (!ok) return;
    try {
      await Promise.all(
        dates.flatMap((d) =>
          payloads.map(({ court, slots }) =>
            axios.post(`${API}/admin/blocks`, {
              court_id: court.court_id,
              booking_date: d,
              time_slots: slots,
              public_note: publicNoteToSend,
              private_note: privateNoteWithAmount,
            })
          )
        )
      );
      toast.success(
        `Blocked ${total} slot(s) across ${payloads.length} court(s)${repeatNote}${amountSuffix}`
      );
      setPublicNote("");
      setPrivateNote("");
      fetchDay();
    } catch {
      toast.error("Failed to block some slots");
    }
  };

  // Multi-court UNBLOCK companion. Walks the same `selections` state and
  // unblocks every currently-selected slot that is in `manually_blocked`
  // status. No notes required (we're clearing, not creating). Recurring
  // not applied — unblocking is a single-date undo by design (the admin
  // can repeat per week explicitly if needed via the Blocks tab).
  const handleUnblockSelectedAcrossCourts = async () => {
    const dateStr = toDateStr(selectedDate);
    const payloads = courtsData
      .map((c) => {
        const sel = selections[c.court_id] || new Set();
        const blockedSlots = c.all_slots
          .filter((s) => sel.has(s.time_slot) && s.status === "manually_blocked")
          .map((s) => s.time_slot);
        return { court: c, slots: blockedSlots };
      })
      .filter(({ slots }) => slots.length > 0);

    if (!payloads.length) {
      toast.error("Select manually-blocked slots first");
      return;
    }
    const total = payloads.reduce((n, { slots }) => n + slots.length, 0);
    const ok = window.confirm(
      `Unblock ${total} slot(s) across ${payloads.length} court(s) on ${dateStr}?`
    );
    if (!ok) return;
    try {
      await Promise.all(
        payloads.map(({ court, slots }) =>
          axios.post(`${API}/admin/blocks/unblock`, {
            court_id: court.court_id,
            booking_date: dateStr,
            time_slots: slots,
          })
        )
      );
      toast.success(`Unblocked ${total} slot(s) across ${payloads.length} court(s)`);
      fetchDay();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `Failed to unblock — ${detail}` : "Failed to unblock some slots");
    }
  };

  const handleBlockAllCourtsDay = async () => {
    const dateStr = toDateStr(selectedDate);
    const courtsToBlock = courtsData
      .map((c) => ({
        court: c,
        available: c.all_slots.filter((s) => s.status === "available").map((s) => s.time_slot),
      }))
      .filter(({ available }) => available.length > 0);
    if (!courtsToBlock.length) {
      toast.error("No available slots to block across any court");
      return;
    }
    if (!privateNote.trim()) {
      toast.error("Private note is required (e.g. client name)");
      return;
    }
    const total = courtsToBlock.reduce((n, { available }) => n + available.length, 0);
    const dates = expandWeeklyDates(dateStr, repeatWeeks);
    const repeatNote = dates.length > 1 ? ` for ${dates.length} weeks` : "";
    const ok = window.confirm(
      `Block the ENTIRE day (${total} slot(s) across ${courtsToBlock.length} court(s))${repeatNote} starting ${dateStr}? Useful for holidays or tournaments.`
    );
    if (!ok) return;
    try {
      await Promise.all(
        dates.flatMap((d) =>
          courtsToBlock.map(({ court, available }) =>
            axios.post(`${API}/admin/blocks`, {
              court_id: court.court_id,
              booking_date: d,
              time_slots: available,
              public_note: publicNote || "Full day",
              private_note: privateNote.trim(),
            })
          )
        )
      );
      toast.success(`Blocked ${total} slot(s) across ${courtsToBlock.length} court(s)${repeatNote}`);
      setPublicNote("");
      setPrivateNote("");
      fetchDay();
    } catch {
      toast.error("Failed to block all courts");
    }
  };

  const handleCancelBooking = async (bookingId, customerName) => {
    const ok = window.confirm(
      `Cancel booking by ${customerName || "customer"}? They will be notified by email and the Google Calendar event removed.`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchDay();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const slotClassName = (status, selected) => {
    if (status === "available") {
      return selected
        ? "bg-[#8cc63f] text-black border-[#8cc63f]"
        : "bg-[#121212] text-white border-white/10 hover:border-[#8cc63f] cursor-pointer";
    }
    if (status === "manually_blocked") {
      return selected
        ? "bg-orange-500 text-black border-orange-500"
        : "bg-orange-500/20 text-orange-300 border-orange-500/40 hover:border-orange-400 cursor-pointer";
    }
    if (status === "booked") {
      return "bg-yellow-400/15 text-yellow-200 border-yellow-400/40 cursor-pointer hover:border-yellow-300";
    }
    if (status === "externally_blocked") {
      return "bg-purple-500/20 text-purple-300 border-purple-500/40 cursor-not-allowed";
    }
    return "bg-[#1a1a1a] text-[#666] border-white/5";
  };

  return (
    <div data-testid="admin-calendar-content">
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-bold">Calendar &amp; Manual Blocks</h2>
        {/* Compact date pill (mobile only) — surfaces the current selection
            so the admin always knows which day they're editing even when the
            full sidebar is collapsed. */}
        <button
          type="button"
          data-testid="mobile-sidebar-toggle"
          onClick={() => setMobileSidebarOpen((o) => !o)}
          className="lg:hidden inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold border border-white/15 rounded-sm hover:border-[#8cc63f] hover:text-[#8cc63f] transition-colors"
        >
          <CalendarIcon size={14} />
          {selectedDate.toLocaleDateString(undefined, { month: "short", day: "numeric", weekday: "short" })}
          <span className="text-[10px] text-[#a1a1aa]">
            {mobileSidebarOpen ? "▲" : "▼"}
          </span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
        {/* Date picker + legend + note */}
        <div
          className={`card-surface p-4 space-y-4 lg:sticky lg:top-24 ${
            mobileSidebarOpen ? "block" : "hidden lg:block"
          }`}
          data-testid="admin-calendar-sidebar"
        >
          <div>
            <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
              <CalendarIcon size={16} /> Date
            </h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiersClassNames={{
                selected: "assenda-day-selected",
                today: "assenda-day-today",
              }}
              className="rounded-md border border-white/10"
            />
          </div>

          <div className="space-y-3">
            <div>
              <label className="label-text block mb-2">
                Public note{" "}
                <span className="text-[#a1a1aa] font-normal">(optional)</span>
              </label>
              <input
                data-testid="block-public-note-input"
                type="text"
                maxLength={80}
                placeholder="e.g. Venue Rental, Tournament/Dual Meet, Social Event"
                value={publicNote}
                onChange={(e) => setPublicNote(e.target.value)}
                className="input-field text-sm"
              />
              <p className="text-[11px] text-[#a1a1aa] mt-1">
                Shown to customers · blank → calendar shows “Booked”
              </p>
            </div>
            <div>
              <label className="label-text block mb-2">
                Private note{" "}
                <span className="text-[#ff6b6b] font-semibold">(mandatory)</span>
              </label>
              <input
                data-testid="block-private-note-input"
                type="text"
                required
                maxLength={120}
                placeholder="e.g. Client name or other remarks"
                value={privateNote}
                onChange={(e) => setPrivateNote(e.target.value)}
                className="input-field text-sm"
              />
              <p className="text-[11px] text-[#a1a1aa] mt-1">
                Internal only · never shown to customers
              </p>
            </div>
            <div>
              <label className="label-text block mb-2">
                Repeat weekly{" "}
                <span className="text-[#a1a1aa] font-normal">(optional)</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="block-repeat-weeks-input"
                  type="number"
                  min={1}
                  max={52}
                  value={repeatWeeks}
                  onChange={(e) =>
                    setRepeatWeeks(
                      Math.max(1, Math.min(52, Number(e.target.value) || 1))
                    )
                  }
                  className="input-field text-sm w-20 text-center"
                />
                <span className="text-xs text-[#a1a1aa]">
                  week{repeatWeeks === 1 ? "" : "s"}
                </span>
              </div>
              {repeatWeeks > 1 && (
                <p
                  data-testid="repeat-preview"
                  className="text-[11px] text-[#8cc63f] mt-1 leading-snug"
                >
                  Block will apply to{" "}
                  {expandWeeklyDates(toDateStr(selectedDate), repeatWeeks)
                    .map((d) => d.slice(5))
                    .join(", ")}
                </p>
              )}
              <p className="text-[11px] text-[#a1a1aa] mt-1">
                1 = today only · 4 = this week + next 3 weeks
              </p>
            </div>
          </div>

          <div className="text-xs space-y-1.5 pt-2 border-t border-white/10">
            <p className="label-text mb-2">Legend</p>
            <Legend color="bg-[#121212] border border-white/10" label="Available" />
            <Legend color="bg-[#8cc63f]" label="Selected" />
            <Legend color="bg-orange-500/20 border border-orange-500/40" label="Manually blocked" />
            <Legend color="bg-yellow-400/15 border border-yellow-400/40" label="Booked (click to cancel)" />
            <Legend color="bg-purple-500/20 border border-purple-500/40" label="Google Calendar" />
          </div>
        </div>

        {/* 4-court grid */}
        <div>
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-xs label-text">Quick actions:</span>
                {(() => {
                  // Aggregate the current selections that target an "available"
                  // slot — these are the slots eligible for a bulk block.
                  // We also sum their `rate` so the admin sees the price they
                  // should collect for the whole selection at a glance, and
                  // the same amount is appended to the note (so it shows up
                  // in the block preview + on the Google Calendar event).
                  let slotCount = 0;
                  let courtCount = 0;
                  let slotAmount = 0;
                  courtsData.forEach((c) => {
                    const sel = selections[c.court_id] || new Set();
                    if (!sel.size) return;
                    const eligible = c.all_slots.filter(
                      (s) => sel.has(s.time_slot) && s.status === "available"
                    );
                    if (eligible.length > 0) {
                      slotCount += eligible.length;
                      courtCount += 1;
                      slotAmount += eligible.reduce(
                        (n, s) => n + (Number(s.rate) || 0),
                        0
                      );
                    }
                  });
                  const amountLabel = slotAmount > 0
                    ? ` · ₱${slotAmount.toLocaleString("en-PH")}`
                    : "";
                  return (
                    <button
                      data-testid="block-selected-across-courts-btn"
                      disabled={slotCount === 0}
                      onClick={handleBlockSelectedAcrossCourts}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#8cc63f]/15 text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Lock size={12} />
                      {slotCount === 0
                        ? "Block selected"
                        : `Block selected (${slotCount} slot${slotCount === 1 ? "" : "s"} · ${courtCount} court${courtCount === 1 ? "" : "s"}${amountLabel}${repeatWeeks > 1 ? ` × ${repeatWeeks} weeks` : ""})`}
                    </button>
                  );
                })()}
                {/* Multi-court unblock — counts selections sitting on
                    `manually_blocked` slots. Disabled until at least one is
                    selected. Confirms with a count, then dispatches one
                    /admin/blocks/unblock call per court in parallel. */}
                {(() => {
                  let slotCount = 0;
                  let courtCount = 0;
                  courtsData.forEach((c) => {
                    const sel = selections[c.court_id] || new Set();
                    if (!sel.size) return;
                    const eligible = c.all_slots.filter(
                      (s) =>
                        sel.has(s.time_slot) && s.status === "manually_blocked"
                    ).length;
                    if (eligible > 0) {
                      slotCount += eligible;
                      courtCount += 1;
                    }
                  });
                  return (
                    <button
                      data-testid="unblock-selected-across-courts-btn"
                      disabled={slotCount === 0}
                      onClick={handleUnblockSelectedAcrossCourts}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-sm hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Unlock size={12} />
                      {slotCount === 0
                        ? "Unblock selected"
                        : `Unblock selected (${slotCount} slot${slotCount === 1 ? "" : "s"} · ${courtCount} court${courtCount === 1 ? "" : "s"})`}
                    </button>
                  );
                })()}
                <button
                  data-testid="block-all-courts-day-btn"
                  onClick={handleBlockAllCourtsDay}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-orange-500/15 text-orange-300 border border-orange-500/40 rounded-sm hover:bg-orange-500 hover:text-black transition-colors"
                >
                  <Zap size={12} /> Block whole day (all courts)
                </button>
                <button
                  data-testid="block-op-am-btn"
                  onClick={handleBlockOPAM}
                  title="Block 8 AM–12 PM on Courts 1, 2, 3 for morning Open Plays"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#8cc63f]/15 text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
                >
                  <Zap size={12} /> OP AM (8 AM–12 PM · C1-3)
                </button>
                <button
                  data-testid="block-op-pm-multi-btn"
                  onClick={handleBlockOPPMMulti}
                  title="Block 5 PM–1 AM on Courts 1, 2, 3 for evening Open Plays"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#8cc63f]/15 text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
                >
                  <Zap size={12} /> OP PM (5 PM–1 AM · C1-3)
                </button>
              </div>

              {/* Mobile court tabs — render one court at a time on small
                  screens so the admin doesn't scroll past 4 × 18 slots.
                  Hidden on `sm:` and up where the multi-column grid kicks in. */}
              <div
                className="sm:hidden -mx-1 flex gap-1 mb-3 overflow-x-auto"
                data-testid="mobile-court-tabs"
              >
                {courtsData.map((c, idx) => (
                  <button
                    key={c.court_id}
                    type="button"
                    data-testid={`mobile-court-tab-${idx}`}
                    onClick={() => setMobileCourtIdx(idx)}
                    className={`flex-1 min-w-[80px] px-2 py-2 text-xs font-bold rounded-sm border transition-colors ${
                      idx === mobileCourtIdx
                        ? "bg-[#8cc63f] text-black border-[#8cc63f]"
                        : "bg-transparent text-[#a1a1aa] border-white/10 hover:border-[#8cc63f]"
                    }`}
                  >
                    {c.court_name.replace(/^Court\s*/i, "C")}
                    {(selections[c.court_id]?.size || 0) > 0 && (
                      <span className="ml-1 text-[10px] opacity-80">
                        ({selections[c.court_id].size})
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {courtsData.map((court, idx) => {
                const sel = selections[court.court_id] || new Set();
                const selCount = sel.size;
                // On mobile (< sm) only render the active tab's court. The
                // hidden class collapses the others to zero — keeps the DOM
                // mounted so selections persist across tab switches.
                const mobileHidden = idx !== mobileCourtIdx ? "hidden sm:block" : "";
                return (
                  <div
                    key={court.court_id}
                    className={`card-surface p-3 ${mobileHidden}`}
                    data-testid={`calendar-court-${court.court_name.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-bold text-[#8cc63f]">
                        {court.court_name}
                      </h3>
                      {selCount > 0 && (
                        <button
                          onClick={() => clearCourtSelection(court.court_id)}
                          className="text-[10px] uppercase tracking-wider text-[#a1a1aa] hover:text-white"
                          data-testid={`clear-selection-${court.court_id}`}
                        >
                          Clear ({selCount})
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:flex sm:flex-col gap-1.5 sm:max-h-[55vh] sm:overflow-y-auto pr-1">
                      {court.all_slots.map((s) => {
                        const selected = sel.has(s.time_slot);
                        const tooltip =
                          s.status === "booked"
                            ? `Booked by ${s.booking?.customer_name || "customer"} — click to cancel`
                            : s.status === "manually_blocked"
                            ? `Manual block${s.note ? ` — ${s.note}` : ""}`
                            : s.status === "externally_blocked"
                            ? "Blocked in Google Calendar"
                            : s.time_slot && formatSlot(s.time_slot);
                        const onClick = () => {
                          if (s.status === "booked" && s.booking?.booking_id) {
                            handleCancelBooking(
                              s.booking.booking_id,
                              s.booking.customer_name
                            );
                            return;
                          }
                          toggleSelect(court.court_id, s.time_slot, s.status);
                        };
                        return (
                          <button
                            key={s.time_slot}
                            type="button"
                            title={tooltip}
                            aria-label={tooltip}
                            data-testid={`cal-slot-${court.court_id}-${s.time_slot}`}
                            data-status={s.status}
                            onClick={onClick}
                            disabled={s.status === "externally_blocked"}
                            className={`text-[11px] px-2 py-2 sm:py-1.5 border rounded-sm font-semibold transition-all flex flex-col items-start sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-1 ${slotClassName(
                              s.status,
                              selected
                            )}`}
                          >
                            <span className="whitespace-nowrap sm:hidden">{formatSlotShort(s.time_slot)}</span>
                            <span className="whitespace-nowrap hidden sm:inline">{formatSlot(s.time_slot)}</span>
                            <span
                              className="text-[9px] uppercase opacity-70 whitespace-nowrap truncate max-w-[110px] sm:max-w-[140px]"
                              title={
                                s.status === "booked"
                                  ? s.booking?.customer_name || "BOOKED"
                                  : s.status === "manually_blocked"
                                  ? s.private_note || s.public_note || s.note || "BLOCKED"
                                  : undefined
                              }
                            >
                              {s.status === "available" && `₱${s.rate}`}
                              {s.status === "booked" &&
                                (s.booking?.customer_name
                                  ? s.booking.customer_name.toUpperCase()
                                  : "BOOKED")}
                              {s.status === "manually_blocked" &&
                                ((s.private_note || s.public_note || s.note)
                                  ? (s.private_note || s.public_note || s.note).toUpperCase()
                                  : "BLOCKED")}
                              {s.status === "externally_blocked" && "GCAL"}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex gap-2 mt-3 pt-3 border-t border-white/10">
                      <button
                        data-testid={`op-btn-${court.court_id}`}
                        title="Block 5 PM–1 AM for Open Plays on the selected date"
                        onClick={() => handleBlockOP(court)}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-bold bg-[#8cc63f]/10 text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
                      >
                        <Zap size={10} /> OP PM
                      </button>
                      <button
                        data-testid={`block-day-btn-${court.court_id}`}
                        title="Block all available slots for this court on the selected date"
                        onClick={() => handleBlockDay(court)}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold bg-transparent text-[#a1a1aa] border border-white/10 rounded-sm hover:border-orange-400 hover:text-orange-300 transition-colors"
                      >
                        <Ban size={10} /> Block day
                      </button>
                      <button
                        data-testid={`unblock-day-btn-${court.court_id}`}
                        title="Clear all manual blocks for this court on the selected date"
                        onClick={() => handleUnblockDay(court)}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold bg-transparent text-[#a1a1aa] border border-white/10 rounded-sm hover:border-[#8cc63f] hover:text-[#8cc63f] transition-colors"
                      >
                        <Unlock size={10} /> Clear day
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className={`inline-block w-3 h-3 rounded-sm ${color}`}></span>
    <span className="text-[11px] text-[#a1a1aa]">{label}</span>
  </div>
);

export default AdminCalendar;
